/* eslint-disable react/jsx-no-undef */
import React, { useRef, useEffect, useState, useCallback } from 'react';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import { useField } from '@unform/core';
import { ErrorMessage } from './styles';

export interface ChipOption {
  value: string | number | boolean;
  label: string;
}

interface ChipSelectProps {
  name: string;
  label?: string;
  options: ChipOption[];
  required?: boolean;
  onChange?: (value: string | number | boolean) => void;
}

const ChipSelect: React.FC<ChipSelectProps> = ({
  name,
  label,
  options,
  required,
  onChange,
}) => {
  const { fieldName, registerField, defaultValue, error } = useField(name);

  // ref evita closure stale no registerField
  const selectedRef = useRef<string | number | boolean>(defaultValue ?? '');
  const [selected, setSelected] = useState<string | number | boolean>(
    defaultValue ?? '',
  );

  // Registra o campo customizado no Unform
  useEffect(() => {
    registerField({
      name: fieldName,
      getValue: () => selectedRef.current,
      setValue: (_ref, value) => {
        selectedRef.current = value;
        setSelected(value);
      },
      clearValue: () => {
        selectedRef.current = '';
        setSelected('');
      },
    });
  }, [fieldName, registerField]);

  const handleSelect = useCallback(
    (value: string | number | boolean) => {
      selectedRef.current = value;
      setSelected(value);
      onChange?.(value);
    },
    [onChange],
  );

  return (
    <FormControl component="fieldset" error={!!error} fullWidth>
      {label && (
        <p className="highlight-label">
          {label}
          {required && <span className="asterisk"> *</span>}
        </p>
      )}

      <div className="chip-group">
        {options.map(option => {
          const isSelected = selected === option.value;
          return (
            <Chip
              key={String(option.value)}
              label={option.label}
              clickable
              color={isSelected ? 'primary' : 'default'}
              variant={isSelected ? 'filled' : 'outlined'}
              onClick={() => handleSelect(option.value)}
            />
          );
        })}
      </div>

      {error && <ErrorMessage>{error}</ErrorMessage>}
    </FormControl>
  );
};

export default ChipSelect;
